// src/pages/Dashboard.tsx
import React, { useEffect, useState } from "react";
import { TextIrisModal } from "./TextIrisModal";
import { ProductSearchModal } from "./ProductSearchModal";
import { AiChatModal } from "./AiChatModal";
import { QuizModal } from "./QuizModal";
import { LiveCcModal } from "./LiveCCModal";
import { UserSearchModal } from "./UserSearchModal";

const AI_API_URL = process.env.REACT_APP_AI_API_URL;

interface FeatureData {
  current: number;
  previous?: number;
  delta_pct?: number;
  avg_call_duration?: string;
}

interface DashboardData {
  voice_iris: FeatureData;
  text_iris: FeatureData;
  product_lens: FeatureData;
  ai_chat: FeatureData;
  quizzes: FeatureData;
  live_cc: FeatureData;
}

const EMPTY_DASHBOARD: DashboardData = {
  voice_iris: { current: 0 },
  text_iris: { current: 0 },
  product_lens: { current: 0 },
  ai_chat: { current: 0 },
  quizzes: { current: 0 },
  live_cc: { current: 0 },
};

// Dummy data for testing
const DUMMY_DATA: DashboardData = {
  voice_iris: {
    current: 1247,
    previous: 1089,
    delta_pct: 14.5,
    avg_call_duration: "2:34",
  },
  text_iris: {
    current: 3891,
    previous: 3654,
    delta_pct: 6.5,
  },
  product_lens: {
    current: 892,
    previous: 1034,
    delta_pct: -13.7,
  },
  ai_chat: {
    current: 15647,
  },
  quizzes: {
    current: 567,
    previous: 423,
    delta_pct: 34.0,
  },
  live_cc: {
    current: 234,
    previous: 198,
    delta_pct: 18.2,
  },
};

const FEATURE_CONFIGS = {
  voice_iris: {
    title: "Voice Iris",
    subtitle: "Number of calls",
    color: "#1f2937",
    accentColor: "#3b82f6",
  },
  text_iris: {
    title: "Text Iris",
    subtitle: "Number of activations",
    color: "#1f2937",
    accentColor: "#8b5cf6",
  },
  product_lens: {
    title: "Product Lens",
    subtitle: "Number of searches",
    color: "#1f2937",
    accentColor: "#06b6d4",
  },
  ai_chat: {
    title: "AI Chat Msg",
    subtitle: "Total from last 7 days",
    color: "#1f2937",
    accentColor: "#10b981",
  },
  quizzes: {
    title: "Quizzes",
    subtitle: "Number of engagements",
    color: "#1f2937",
    accentColor: "#f59e0b",
  },
  live_cc: {
    title: "Live CC",
    subtitle: "Number of activations",
    color: "#1f2937",
    accentColor: "#ef4444",
  },
};

interface FeatureCardProps {
  featureKey: keyof DashboardData;
  data: FeatureData;
  config: typeof FEATURE_CONFIGS[keyof typeof FEATURE_CONFIGS];
  isLoading: boolean;
  onClick: (key: keyof DashboardData) => void;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  featureKey,
  data,
  config,
  isLoading,
  onClick,
}) => {
  const [animateValue, setAnimateValue] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!isLoading && data) {
      const timer = setTimeout(() => {
        setShowContent(true);
        const duration = 1800;
        const steps = 60;
        const increment = data.current / steps;
        let step = 0;

        const counter = setInterval(() => {
          step += 1;
          setAnimateValue(Math.min(increment * step, data.current));
          if (step >= steps) {
            clearInterval(counter);
            setAnimateValue(data.current);
          }
        }, duration / steps);

        return () => {
          clearInterval(counter);
          clearTimeout(timer);
        };
      }, 800);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [isLoading, data]);

  const formatDelta = (delta?: number) => {
    if (delta === undefined || delta === null) return null;
    const sign = delta > 0 ? "+" : "";
    return `${sign}${delta.toFixed(1)}%`;
  };

  const getDeltaColor = (delta?: number) => {
    if (delta === undefined || delta === null) return "#64748b";
    return delta > 0 ? "#059669" : delta < 0 ? "#dc2626" : "#64748b";
  };

  return (
    <div
        onClick={() => onClick(featureKey)}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(featureKey);
            }
        }}
        role="button"
        tabIndex={0}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        padding: "32px",
        boxShadow: isHovered
            ? "0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)"
            : "0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.1)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: isHovered ? "translateY(-8px) scale(1.02)" : "translateY(0px) scale(1)",
        cursor: "pointer",
        minHeight: "240px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        }}
    >
      {/* Gradient border effect */}
      <div
        style={{
          position: "absolute",
          inset: "-1px",
          borderRadius: "16px",
          padding: "1px",
          background: `linear-gradient(45deg, ${config.accentColor}20, transparent, ${config.accentColor}20)`,
          WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "subtract",
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.4s ease",
        }}
      />

      {/* Professional Circle Loading Animation */}
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {/* Main circle progress */}
          <div style={{ position: "relative" }}>
            <svg
              width="80"
              height="80"
              style={{
                transform: "rotate(-90deg)",
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
              }}
            >
              {/* Background circle */}
              <circle
                cx="40"
                cy="40"
                r="32"
                fill="none"
                stroke="#f3f4f6"
                strokeWidth="4"
              />
              {/* Animated progress circle */}
              <circle
                cx="40"
                cy="40"
                r="32"
                fill="none"
                stroke={config.accentColor}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="201"
                strokeDashoffset="201"
                style={{
                  animation: "drawCircle 2.5s cubic-bezier(0.4, 0, 0.2, 1) forwards",
                  filter: `drop-shadow(0 0 6px ${config.accentColor}40)`,
                }}
              />
              {/* Inner rotating dot */}
              <circle
                cx="40"
                cy="8"
                r="3"
                fill={config.accentColor}
                style={{
                  transformOrigin: "40px 40px",
                  animation: "rotateDot 2s linear infinite",
                  opacity: 0.8,
                }}
              />
            </svg>
          </div>
          
          {/* Loading text */}
          <div
            style={{
              color: "#6b7280",
              fontSize: "13px",
              fontWeight: "500",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            Loading
          </div>
        </div>
      )}

      {/* Content */}
      <div
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          textAlign: "center",
          width: "100%",
        }}
      >
        {/* Title */}
        <div style={{ marginBottom: "24px" }}>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#ffffff",
              letterSpacing: "-0.025em",
            }}
          >
            {config.title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.7)",
              fontWeight: "400",
            }}
          >
            {config.subtitle}
          </p>
        </div>

        {/* Main Number with Circle Background */}
        <div
        style={{
            marginBottom: "20px",
        }}
        >
        <div
            style={{
            fontSize: "42px",
            fontWeight: "700",
            color: "#ffffff",
            fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
            letterSpacing: "-0.025em",
            lineHeight: "1",
            }}
        >
            {Math.round(animateValue).toLocaleString()}
        </div>
        </div>

        {/* Delta and Extra Info */}
        <div style={{ minHeight: "32px" }}>
          {data.delta_pct !== undefined && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "6px 12px",
                borderRadius: "8px",
                backgroundColor: `${getDeltaColor(data.delta_pct)}10`,
                border: `1px solid ${getDeltaColor(data.delta_pct)}20`,
                fontSize: "13px",
                fontWeight: "600",
                color: getDeltaColor(data.delta_pct),
              }}
            >
              <span style={{ marginRight: "4px" }}>
                {data.delta_pct > 0 ? "↗" : data.delta_pct < 0 ? "↘" : "→"}
              </span>
              {formatDelta(data.delta_pct)} since last week
            </div>
          )}
          
          {/* {featureKey === "voice_iris" && data.avg_call_duration && (
            <div
              style={{
                marginTop: "8px",
                fontSize: "12px",
                color: "#6b7280",
                fontWeight: "500",
              }}
            >
              Avg duration: {data.avg_call_duration}
            </div>
          )} */}
        </div>
      </div>

      {/* Click to explore indicator */}
      <div
        style={{
          position: "absolute",
          bottom: "16px",
          right: "16px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          opacity: isHovered ? 1 : 0.3,
          transition: "opacity 0.3s ease",
        }}
      >
        <span
          style={{
            fontSize: "11px",
            color: "#9ca3af",
            fontWeight: "500",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Explore
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
          style={{ color: "#9ca3af" }}
        >
          <path d="M4.5 3a.5.5 0 0 0 0 1h5.793L2.146 12.146a.5.5 0 0 0 .708.708L10.5 4.207V10a.5.5 0 0 0 1 0V3a.5.5 0 0 0-.5-.5H4.5Z" />
        </svg>
      </div>
    </div>
  );
};


interface VoiceIrisModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VoiceIrisModal: React.FC<VoiceIrisModalProps> = ({ isOpen, onClose }) => {
  const [selectedDays, setSelectedDays] = useState(7);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hoveredBar, setHoveredBar] = useState<{index: number, count: number, date: string} | null>(null);

  // Dummy data structure
  const DUMMY_VOICE_DATA = {
    1: { total: 45, avg_mmss: "2:15", history: [["2024-01-20", 45]] },
    7: { 
      total: 287, 
      avg_mmss: "2:34", 
      history: [
        ["2024-01-14", 35],
        ["2024-01-15", 42],
        ["2024-01-16", 38],
        ["2024-01-17", 51],
        ["2024-01-18", 29],
        ["2024-01-19", 47],
        ["2024-01-20", 45]
      ]
    },
    14: { 
      total: 612, 
      avg_mmss: "2:28", 
      history: [
        ["2024-01-07", 41], ["2024-01-08", 38], ["2024-01-09", 44], ["2024-01-10", 52], 
        ["2024-01-11", 33], ["2024-01-12", 39], ["2024-01-13", 46], ["2024-01-14", 35],
        ["2024-01-15", 42], ["2024-01-16", 38], ["2024-01-17", 51], ["2024-01-18", 29],
        ["2024-01-19", 47], ["2024-01-20", 45]
      ]
    },
    30: { 
      total: 1247, 
      avg_mmss: "2:31", 
      history: [
        ["2024-01-01", 39], ["2024-01-02", 42], ["2024-01-03", 35], ["2024-01-04", 48],
        ["2024-01-05", 31], ["2024-01-06", 44], ["2024-01-07", 41], ["2024-01-08", 38],
        ["2024-01-09", 44], ["2024-01-10", 52], ["2024-01-11", 33], ["2024-01-12", 39],
        ["2024-01-13", 46], ["2024-01-14", 35], ["2024-01-15", 42], ["2024-01-16", 38],
        ["2024-01-17", 51], ["2024-01-18", 29], ["2024-01-19", 47], ["2024-01-20", 45],
        ["2024-01-21", 40], ["2024-01-22", 36], ["2024-01-23", 43], ["2024-01-24", 49],
        ["2024-01-25", 32], ["2024-01-26", 45], ["2024-01-27", 38], ["2024-01-28", 41],
        ["2024-01-29", 37], ["2024-01-30", 44]
      ]
    }
  };

//   useEffect(() => {
//     if (isOpen) {
//       setLoading(true);
//       setTimeout(() => {
//         setData(DUMMY_VOICE_DATA[selectedDays as keyof typeof DUMMY_VOICE_DATA]);
//         setLoading(false);
//       }, 800);
//     }
//   }, [isOpen, selectedDays]);

    useEffect(() => {
    if (!isOpen) {
        return () => {}; // no-op cleanup
    }

    const controller = new AbortController();
    setLoading(true);

    // http://127.0.0.1:8000
    fetch(
        `${AI_API_URL}/fetch-voice-iris-stats?days=${selectedDays}`, 
        { signal: controller.signal }
    )
        .then(r => {
        if (!r.ok) throw new Error("Voice Iris fetch failed");
        return r.json();
        })
        .then(json => {
        setData({
            total: json.current,
            avg_mmss: json.avg_call_duration,
            history: json.history,
        });
        })
        .catch(err => {
        if (err.name !== "AbortError") console.error(err);
        })
        .finally(() => setLoading(false));

    return () => controller.abort();
    }, [isOpen, selectedDays]);


  const maxValue = data ? Math.max(...data.history.map((item: any) => item[1])) : 0;

  if (!isOpen) return null;

  return (
    <div
    role="presentation"
    style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(4px)",
        animation: isOpen ? "fadeIn 0.3s ease-out" : "fadeOut 0.3s ease-out",
    }}
    onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
    }}
    onKeyDown={(e) => {
        if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        }
    }}
    tabIndex={-1}
    >
        <div
        role="dialog"
        aria-modal="true"
        style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            padding: "32px",
            width: "90%",
            maxWidth: "800px",
            maxHeight: "90vh",
            overflowY: "auto",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            animation: isOpen ? "zoomIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)" : "",
        }}
        >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
          <div>
            <h2 style={{ color: "#ffffff", fontSize: "24px", fontWeight: "700", margin: 0 }}>
              Voice Iris Analytics
            </h2>
            <p style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px", margin: "4px 0 0 0" }}>
              Detailed conversation statistics
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "8px",
              color: "#ffffff",
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Time Period Selector */}
        <div style={{ marginBottom: "24px" }}>
        {/* Heading text */}
        <p
            style={{
            margin: 0,
            marginBottom: 8,
            color: "rgba(255, 255, 255, 0.8)",
            fontSize: 14,
            fontWeight: 500,
            }}
        >
            Time&nbsp;Period
        </p>

        <select
        id="voice-period"
        aria-label="Time period"
        value={selectedDays}
        onChange={(e) => setSelectedDays(Number(e.target.value))}
        style={{
            display: "block",
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 8,
            color: "#ffffff",
            padding: "8px 12px",
            fontSize: 14,
            outline: "none",
        }}
        >
        <option value={1} style={{ background: "#1f2937", color: "#ffffff" }}>Last 24 hours</option>
        <option value={7} style={{ background: "#1f2937", color: "#ffffff" }}>Last 7 days</option>
        <option value={14} style={{ background: "#1f2937", color: "#ffffff" }}>Last 14 days</option>
        <option value={30} style={{ background: "#1f2937", color: "#ffffff" }}>Last 30 days</option>
        </select>
        </div>

        {/* Chart */}
        <div style={{ marginBottom: "32px" }}>
        <h3 style={{ color: "#ffffff", fontSize: "18px", fontWeight: "600", marginBottom: "16px" }}>
            Daily Call Volume
        </h3>
        <div
            style={{
            background: "rgba(255, 255, 255, 0.05)",
            borderRadius: "12px",
            padding: "24px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            height: "300px",
            display: "flex",
            position: "relative",
            }}
        >
            {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", color: "rgba(255, 255, 255, 0.7)" }}>
                Loading chart...
            </div>
            ) : (
            <>
                {/* Y-Axis */}
                <div
                style={{
                    width: "50px",
                    height: "240px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    paddingRight: "12px",
                    marginRight: "16px",
                    borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                }}
                >
                {[...Array(6)].map((_, i) => {
                    const value = Math.round((maxValue * (5 - i)) / 5);
                    return (
                    <div
                        key={i}
                        style={{
                        color: "rgba(255, 255, 255, 0.6)",
                        fontSize: "11px",
                        fontWeight: "500",
                        textAlign: "right",
                        lineHeight: "1",
                        }}
                    >
                        {value > 0 ? value : 0}
                    </div>
                    );
                })}
                </div>

                {/* Chart Area */}
                <div
                style={{
                    flex: 1,
                    height: "240px",
                    display: "flex",
                    alignItems: "end",
                    justifyContent: "space-around",
                    gap: "4px",
                    position: "relative",
                }}
                >
                {/* Horizontal grid lines */}
                {[...Array(6)].map((_, i) => (
                    <div
                    key={i}
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: `${(i * 240) / 5}px`,
                        height: "1px",
                        background: "rgba(255, 255, 255, 0.05)",
                        pointerEvents: "none",
                    }}
                    />
                ))}

                {/* Bars */}
                {data?.history.map(([date, count]: any, index: number) => {
                const height = (count / maxValue) * 240;

                return (
                    <div
                    key={date}
                    style={{
                        position: "relative",
                        flex: 1,
                        height: "100%",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                    }}
                    >
                    {/* BAR */}
                    <div
                        style={{
                        height: `${height}px`,
                        width: "100%",
                        maxWidth: 40,
                        background: "linear-gradient(to top,#3b82f6,#60a5fa)",
                        borderRadius: "4px 4px 0 0",
                        boxShadow: "0 4px 12px rgba(59,130,246,.3)",
                        animation: `barGrowUp 1s ease-out ${index * 0.1}s both`,
                        cursor: "pointer",
                        }}
                        onMouseEnter={() => setHoveredBar({ index, count, date })}
                        onMouseLeave={() => setHoveredBar(null)}
                    />

                    {hoveredBar?.index === index && (
                        <div
                        style={{
                            position: "absolute",
                            bottom: `${height + 10}px`,
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "rgba(0,0,0,.8)",
                            color: "#60a5fa",
                            padding: "4px 8px",
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            pointerEvents: "none",
                        }}
                        >
                        {count}
                        </div>
                    )}

                    <div
                        style={{
                        position: "absolute",
                        bottom: -18,
                        left: "50%",
                        transform: "translateX(-50%)",
                        color: "rgba(255,255,255,.6)",
                        fontSize: 10,
                        lineHeight: 1.2,
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        }}
                    >
                        {new Date(date).toLocaleDateString([], { month: "short", day: "numeric" })}
                    </div>
                    </div>
                );
                })}
                </div>
            </>
            )}
        </div>
        </div>

        {/* Summary Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.08)",
              borderRadius: "12px",
              padding: "20px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "center",
            }}
          >
            <div style={{ color: "#60a5fa", fontSize: "28px", fontWeight: "700", marginBottom: "4px" }}>
              {data?.total.toLocaleString() || 0}
            </div>
            <div style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>Total Calls</div>
          </div>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.08)",
              borderRadius: "12px",
              padding: "20px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "center",
            }}
          >
            <div style={{ color: "#60a5fa", fontSize: "28px", fontWeight: "700", marginBottom: "4px" }}>
              {data?.avg_mmss || "0:00"}
            </div>
            <div style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>Avg Duration</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [selectedFeature, setSelectedFeature] = useState<keyof DashboardData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);


    useEffect(() => {
    const controller = new AbortController();

    const fetchDashboardData = async () => {
        try {
        setLoading(true);
        setError("");

        const res = await fetch(
            `${AI_API_URL}/feature-previews?days=7`,
            { signal: controller.signal }
        );
        if (!res.ok) throw new Error("Failed to load dashboard data");
        const json = await res.json();

        // json already looks like DUMMY_DATA → put it straight in state
        setData(json);
        } catch (err: any) {
        if (err.name !== "AbortError") {
            setError(err.message || "Failed to load dashboard data");
            // fall back to EMPTY_DASHBOARD so cards don’t explode
            setData(EMPTY_DASHBOARD);
        }
        } finally {
        setLoading(false);
        }
    };

    fetchDashboardData();
    return () => controller.abort();
    }, []);

    const handleCardClick = (featureKey: keyof DashboardData) => {
    console.log(`Opening detailed view for: ${featureKey}`);
    setSelectedFeature(featureKey);
    setIsModalOpen(true);
    };

  return (
    <>
        <style>
        {`
            @keyframes drawCircle {
            0% { stroke-dashoffset: 201; }
            100% { stroke-dashoffset: 0; }
            }

            @keyframes rotateDot {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
            }

            @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
            }

            @keyframes zoomIn {
            0% { opacity: 0; transform: scale(0.8); }
            100% { opacity: 1; transform: scale(1); }
            }

            @keyframes barGrowUp {
            0% { 
                transform: scaleY(0);
                transform-origin: bottom;
            }
            100% { 
                transform: scaleY(1);
                transform-origin: bottom;
            }
            }

            @media (max-width: 1024px) {
            .dashboard-grid {
                grid-template-columns: repeat(2, 1fr) !important;
            }
            }

            @media (max-width: 640px) {
            .dashboard-grid {
                grid-template-columns: 1fr !important;
            }
            }
        `}
        </style>
      
        <div
        style={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #0b0c1b 0%, #1e293b 100%)",
            padding: "40px 24px",
        }}
        >
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* Header */}
          <div style={{ marginBottom: "48px", textAlign: "center" }}>
            <h1
            style={{
                fontSize: "32px",
                fontWeight: "700",
                color: "#ffffff",
                margin: "0 0 12px 0",
                letterSpacing: "-0.025em",
            }}
            >
            AI Analytics Dashboard
            </h1>
            <p
            style={{
                fontSize: "16px",
                color: "rgba(255,255,255,0.7)",
                margin: 0,
                fontWeight: "400",
            }}
            >
            Real-time performance metrics across all AI features
            </p>
          </div>

          {/* Error State */}
          {error && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "32px",
                color: "#b91c1c",
                fontSize: "14px",
                fontWeight: "500",
                textAlign: "center",
              }}
            >
              ⚠️ {error}
            </div>
          )}

          {/* Dashboard Grid */}
          <div
            className="dashboard-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "32px",
            }}
          >
            {Object.entries(FEATURE_CONFIGS).map(([key, config]) => (
              <FeatureCard
                key={key}
                featureKey={key as keyof DashboardData}
                data={data?.[key as keyof DashboardData] || { current: 0 }}
                config={config}
                isLoading={loading}
                onClick={handleCardClick}
              />
            ))}
          </div>

            {/* ────── User Personalization Section ────── */}
            <div 
            style={{ 
                display: "flex", 
                justifyContent: "center",
                marginTop: "48px",
                marginBottom: "24px"
            }}
            >
            <button
                type="button"
                onClick={() => setShowUserModal(true)}
                style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                background: "rgba(255, 255, 255, 0.08)",
                backdropFilter: "blur(10px)",
                color: "rgba(255, 255, 255, 0.8)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                }}
                onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.12)";
                e.currentTarget.style.color = "#ffffff";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.25)";
                }}
                onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)";
                e.currentTarget.style.color = "rgba(255, 255, 255, 0.8)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)";
                }}
            >
                User Personalization Dashboard
                <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                >
                <path d="M7 17L17 7" />
                <path d="M7 7h10v10" />
                </svg>
            </button>
            </div>

          {/* Footer */}
            <div
            style={{
                textAlign: "center",
                marginTop: "48px", // Reduced from 64px since we have the user section above
                padding: "24px",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)", // Updated to match theme
            }}
            >
            <div
                style={{
                color: "rgba(255, 255, 255, 0.6)", // Updated to match theme
                fontSize: "13px",
                fontWeight: "500",
                marginBottom: "8px",
                }}
            >
                Last updated: {new Date().toLocaleString()}
            </div>
            <div
                style={{
                color: "rgba(255, 255, 255, 0.4)", // Updated to match theme
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "1px",
                }}
            >
                AI Analytics Platform
            </div>
            </div>
        </div>
      </div>

      {/* Modal */}
      {selectedFeature === 'voice_iris' && (
        <VoiceIrisModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedFeature(null);
          }}
        />
      )}

      {selectedFeature === 'text_iris' && (
        <TextIrisModal
            isOpen={isModalOpen}
            onClose={() => {
            setIsModalOpen(false);
            setSelectedFeature(null);
            }}
        />
      )}

      {selectedFeature === 'product_lens' && (
        <ProductSearchModal
            isOpen={isModalOpen}
            onClose={() => {
            setIsModalOpen(false);
            setSelectedFeature(null);
            }}
        />
      )}

    {selectedFeature === 'ai_chat' && (
    <AiChatModal
        isOpen={isModalOpen}
        onClose={() => {
        setIsModalOpen(false);
        setSelectedFeature(null);
        }}
    />
    )}

    {selectedFeature === 'quizzes' && (
    <QuizModal
        isOpen={isModalOpen}
        onClose={() => {
        setIsModalOpen(false);
        setSelectedFeature(null);
        }}
    />
    )}

    {selectedFeature === 'live_cc' && (
    <LiveCcModal
        isOpen={isModalOpen}
        onClose={() => {
        setIsModalOpen(false);
        setSelectedFeature(null);
        }}
    />
    )}


    {showUserModal && (
    <UserSearchModal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
    />
    )}
    </>
  );
}